import {
  AfterViewInit,
  Component,
  HostListener, Inject,
  Input, OnInit, Optional,
  ViewChild
} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {
  EVENT_CALENDAR_ROUTE,
  EVENT_DETAILS, FINISHED_EVENTS_ROUTE, PRIVATE_EVENT_CALENDAR_ROUTE, PRIVATE_FINISHED_EVENTS_ROUTE
} from "src/app/app-routing.module";
import {buildEventSlug} from "@utility";
import {environment} from "@environment";
import {AbstractComponent} from "@components";
import {
  EventItem, Lookups, CalendarViewType
} from "@model";
import {SwiperComponent} from "swiper/angular";
import {Subject} from "rxjs";
import {AuthenticationService} from '@services';
import {Meta, Title} from "@angular/platform-browser";
import {DOCUMENT} from "@angular/common";

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
  standalone: false
})
export class LandingComponent extends AbstractComponent implements AfterViewInit, OnInit {

  @Input() isLanding: boolean = false;
  @ViewChild('upcomingEventsSwiper', {static: false}) upcomingEventsSwiper?: SwiperComponent;
  @ViewChild('newEventsSwiper', {static: false}) newEventsSwiper?: SwiperComponent;

  @Input() upcomingItems: EventItem[] | null = null;
  @Input() newItems: EventItem[] | null = null;
  @Input() countries: Lookups[] = [];
  @Input({required: true}) languages: Lookups[] = [];

  searchQuery = new Subject();
  page: number = 1;
  environment: any = environment;
  breakpoints = {
    1400: {slidesPerView: 3, spaceBetween: 0},
    992: {slidesPerView: 3, spaceBetween: 0},
    576: {slidesPerView: 2, spaceBetween: 0},
    320: {slidesPerView: 1.05, spaceBetween: 10}
  };

  public currentWindowWidth: number = 0;

  sports = [
    {id: 30, imgSrc: 'assets/icons/icons-alt/running_road.svg', buttonTextKey: 'app.common.sports.30'},
    {id: 36, imgSrc: 'assets/icons/icons-alt/trail_running.svg', buttonTextKey: 'app.common.sports.36'},
    {id: 35, imgSrc: 'assets/icons/icons-alt/swimming.svg', buttonTextKey: 'app.common.sports.35'},
    {id: undefined, imgSrc: '', buttonTextKey: 'app.common.sports.43'}
  ];


  swiperStates = {
    upcoming: {currentSlide: 1, totalSlides: 1},
    newEvents: {currentSlide: 1, totalSlides: 1}
  };

  swiperReady = {
    upcoming: false,
    newEvents: false
  };

  constructor(private router: Router, private route: ActivatedRoute, public authenticationService: AuthenticationService,
              private metaService: Meta, private titleService: Title, @Inject(DOCUMENT) public document: Document,
              @Optional() @Inject('DEVICE_TYPE') public ssrDeviceType?: string // <-- inject DEVICE_TYPE
  ) {
    super();
    if (this.isBrowser) {
      this.currentWindowWidth = window.innerWidth;
    }
  }

  ngOnInit(): void {
    this.setSEOData();
  }

  setSEOData(): void {
    const origin = this.document.location.origin;
    const url = `${origin}${this.document.location.pathname}`;
    const description = 'Find the race that inspires you to move';
    const heroImage = `${origin}/assets/img/hero2-mobile.webp`;

    this.titleService.setTitle(`${environment.site_name} | ${description}`);
    this.metaService.updateTag({name: 'description', content: description});

    this.metaService.updateTag({property: 'og:url', content: url});
    this.metaService.updateTag({property: 'og:title', content: environment.site_name});
    this.metaService.updateTag({property: 'og:type', content: 'website'});
    this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
    this.metaService.updateTag({property: 'og:description', content: description});
    this.metaService.updateTag({property: 'og:image', content: heroImage});

    this.metaService.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.metaService.updateTag({name: 'twitter:title', content: environment.site_name});
    this.metaService.updateTag({name: 'twitter:description', content: description});
    this.metaService.updateTag({name: 'twitter:image', content: heroImage});

    this.setCanonicalUrl(url);
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  ngAfterViewInit(): void {
    this.updateSlideCounter(this.upcomingEventsSwiper, 'upcoming');
    this.updateSlideCounter(this.newEventsSwiper, 'newEvents');
  }

  @HostListener('window:resize')
  onResize(): void {
    this.currentWindowWidth = window.innerWidth;
  }

  goToEventDetailsPage(item: EventItem) {
    this.router.navigate([EVENT_DETAILS, buildEventSlug(item.id!, item)], {
      relativeTo: this.route,
    });
  }

  trackByFn(_: number, item: EventItem) {
    return item.id;
  }

  exploreAll() {
    this.router.navigate([EVENT_CALENDAR_ROUTE]);
  }

  slideNext(swiperComponent: SwiperComponent | undefined, key: keyof typeof this.swiperStates) {
    if (!swiperComponent || this.isLastSlideActive(swiperComponent)) return;
    swiperComponent.swiperRef.slideNext(300);
    this.updateSlideCounter(swiperComponent, key);
  }

  slidePrev(swiperComponent: SwiperComponent | undefined, key: keyof typeof this.swiperStates) {
    if (!swiperComponent || this.isFirstSlideActive(swiperComponent)) return;
    swiperComponent.swiperRef.slidePrev(300);
    this.updateSlideCounter(swiperComponent, key);
  }

  isLastSlideActive(swiper?: SwiperComponent): boolean {
    if (!swiper || !swiper.swiperRef) return false;

    const swiperInstance = swiper.swiperRef;

    const slidesPerView = this.getCurrentSlidesPerView();

    const lastVisibleIndex = swiperInstance.slides.length - slidesPerView;

    return swiperInstance.activeIndex >= lastVisibleIndex;
  }

  getCurrentSlidesPerView(): number {
    if (this.currentWindowWidth >= 1400) {
      return 3;
    } else if (this.currentWindowWidth >= 992) {
      return 3;
    } else if (this.currentWindowWidth >= 576) {
      return 2;
    } else {
      return 1.5;
    }
  }

  isFirstSlideActive(swiper?: SwiperComponent): boolean {
    if (!swiper || !swiper.swiperRef) return true;
    return swiper.swiperRef.activeIndex === 0;
  }

  explore(sportId?: number) {
    const queryParams = sportId ? {sports: sportId} : {};
    this.router.navigate([EVENT_CALENDAR_ROUTE], {queryParams});
  }

  updateSlideCounter(swiper?: SwiperComponent, key?: keyof typeof this.swiperStates) {
    if (!swiper || !swiper.swiperRef || !key) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const totalSlides = swiper.swiperRef.slides.length - slidesPerView + 1;

    this.swiperStates[key].totalSlides = Math.max(1, Math.ceil(totalSlides));
    this.swiperStates[key].currentSlide = swiper.swiperRef.realIndex + 1;
  }

  onSwiperReady(swiper: any, key: keyof typeof this.swiperStates) {
    this.swiperReady[key] = true;
    swiper.on('slidesLengthChange', () => {
      this.updateSlideCounterFromInstance(swiper, key);
    });

    swiper.on('resize', () => {
      this.updateSlideCounterFromInstance(swiper, key);
    });

    this.updateSlideCounterFromInstance(swiper, key);
  }

  updateSlideCounterFromInstance(swiperInstance: any, key: keyof typeof this.swiperStates) {
    if (!swiperInstance || !key) return;

    const slidesPerView = this.getCurrentSlidesPerView();
    const totalSlides = swiperInstance.slides.length - slidesPerView + 1;

    this.swiperStates[key].totalSlides = Math.max(1, Math.ceil(totalSlides));
    this.swiperStates[key].currentSlide = swiperInstance.realIndex + 1;
  }

  goToFinished() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_FINISHED_EVENTS_ROUTE : FINISHED_EVENTS_ROUTE]);
  }

  onEventCalendar() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_EVENT_CALENDAR_ROUTE : EVENT_CALENDAR_ROUTE]);
  }

  protected readonly CalendarViewType = CalendarViewType;

}
